import React, { useState } from 'react';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { EMAIL_COLORS } from '../../../shared/constants/brandColors';

const FREQ_LABELS = { weekly: 'weekly', 'bi-weekly': 'every 2 weeks', monthly: 'monthly' };

const InviteMemberModal = ({ group, onClose }) => {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const inviteLink = `${window.location.origin}/join/${group.id}`;
  const amount = `$${parseFloat(group.contribution_amount_per_hand || 0).toFixed(2)}`;
  const freq = FREQ_LABELS[group.contribution_frequency] || group.contribution_frequency;

  const waMessage = [
    `You're invited to join *${group.group_name}* on EsuSol!`,
    '',
    `Contribution: ${amount} per hand, paid ${freq}`,
    '',
    'Tap the link to register and join:',
    inviteLink,
  ].join('\n');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const handleSendEmail = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;

    setSending(true);
    setEmailResult(null);

    const html = `
<!DOCTYPE html>
<html>
<body style="margin:0;padding:0;background:${EMAIL_COLORS.pageBg};font-family:sans-serif;">
  <div style="max-width:520px;margin:40px auto;background:${EMAIL_COLORS.surface};border-radius:12px;overflow:hidden;border:1px solid ${EMAIL_COLORS.border};">
    <div style="background:${EMAIL_COLORS.brand};padding:24px 32px;">
      <h1 style="color:${EMAIL_COLORS.onBrand};margin:0;font-size:20px;">EsuSol</h1>
    </div>
    <div style="padding:28px 32px;">
      <h2 style="color:${EMAIL_COLORS.heading};font-size:17px;margin:0 0 8px;">You're invited to join ${group.group_name}</h2>
      <p style="color:${EMAIL_COLORS.muted};font-size:14px;margin:0 0 20px;">
        You've been invited to join a savings group on EsuSol.
      </p>
      <table style="width:100%;border-collapse:collapse;background:${EMAIL_COLORS.tableBg};border-radius:8px;margin-bottom:20px;">
        <tr>
          <td style="padding:9px 14px;color:${EMAIL_COLORS.muted};font-size:13px;">Group</td>
          <td style="padding:9px 14px;color:${EMAIL_COLORS.heading};font-size:13px;font-weight:600;">${group.group_name}</td>
        </tr>
        <tr style="background:${EMAIL_COLORS.tableStripe};">
          <td style="padding:9px 14px;color:${EMAIL_COLORS.muted};font-size:13px;">Contribution</td>
          <td style="padding:9px 14px;color:${EMAIL_COLORS.heading};font-size:13px;font-weight:600;">${amount} per hand</td>
        </tr>
        <tr>
          <td style="padding:9px 14px;color:${EMAIL_COLORS.muted};font-size:13px;">Frequency</td>
          <td style="padding:9px 14px;color:${EMAIL_COLORS.heading};font-size:13px;font-weight:600;">${freq}</td>
        </tr>
      </table>
      <div style="text-align:center;">
        <a href="${inviteLink}" style="display:inline-block;background:${EMAIL_COLORS.button};color:${EMAIL_COLORS.onButton};padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:600;font-size:15px;">
          Join This Group →
        </a>
      </div>
    </div>
  </div>
</body>
</html>`.trim();

    try {
      const { data: { user } } = await supabaseClient.auth.getUser();

      const { error: fnError } = await supabaseClient.functions.invoke('send-invite-email', {
        body: {
          to: trimmed,
          subject: `You're invited to join ${group.group_name} on EsuSol`,
          html,
          text: waMessage,
        },
      });

      const sent = !fnError;
      setEmailResult({ sent, email: trimmed });

      await supabaseClient.from('audit_logs').insert({
        event_category: 'member_management',
        event_type: 'invite_sent',
        actor_id: user?.id ?? null,
        actor_role: 'admin',
        new_value: {
          group_id: group.id,
          email: trimmed,
          email_sent: sent,
          invite_link: inviteLink,
        },
        risk_level: 'low',
        flagged: false,
        timestamp: new Date().toISOString(),
      });

      if (sent) setEmail('');
    } catch {
      setEmailResult({ sent: false, email: trimmed });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-neutral-100 rounded-lg w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-200">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Invite Member</h2>
            <p className="text-xs text-neutral-500 mt-0.5">{group.group_name}</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">✕</button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Copy invite link */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Group Invite Link</label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                readOnly
                value={inviteLink}
                onClick={e => e.target.select()}
                className="flex-1 px-3 py-2 text-sm border border-neutral-200 rounded-lg bg-neutral-50 text-neutral-600 truncate"
              />
              <button
                onClick={handleCopy}
                className="flex-shrink-0 px-3 py-2 text-sm border border-neutral-300 rounded-lg hover:bg-neutral-50 text-neutral-700 whitespace-nowrap"
              >
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
          </div>

          {/* WhatsApp */}
          <a
            href={`https://wa.me/?text=${encodeURIComponent(waMessage)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-success-600 text-white rounded-lg font-medium hover:bg-success-700 text-sm"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            <span>Share via WhatsApp</span>
          </a>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Send Invite by Email</label>
            <div className="flex space-x-2">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="member@example.com"
                className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                onKeyDown={e => e.key === 'Enter' && handleSendEmail()}
              />
              <button
                onClick={handleSendEmail}
                disabled={sending || !email.trim()}
                className="px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap"
              >
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
            {emailResult && (
              <p className={`mt-2 text-xs ${emailResult.sent ? 'text-success-600' : 'text-error-600'}`}>
                {emailResult.sent
                  ? `Invite sent to ${emailResult.email}`
                  : `Could not deliver email to ${emailResult.email}. Copy the link and share manually.`}
              </p>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-neutral-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 text-sm font-medium hover:bg-neutral-50"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteMemberModal;
